import { useDecision } from '@optimizely/react-sdk'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { useState } from 'react'

import { CookieRegistry } from '@/constants/cookieRegistry'
import { HTTP_STATUS } from '@/constants/httpStatus'
import { ProvinceAbbreviation, useGetGeocodeReverseFromIpQuery } from '@/generated'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { FEATURE_FLAG } from '@/lib/optimizely'
import { createCookie } from '@/utils/cookies/createCookie'
import { getCookieByKey } from '@/utils/cookies/getCookieByKey'
import { getGqlErrorStatusCode } from '@/utils/errors'
import { isServer } from '@/utils/isSSR'
import { sendToLogger } from '@/utils/sendToLogger'

import { LinkCustom } from '../link-custom/LinkCustom'
import {
  CookieBannerBackgroundOverlay,
  CookieBannerCloseButton,
  CookieBannerContent,
  CookieBannerDescription,
} from './styled'

export const CookieAcknowledgementBanner = () => {
  const [featureToggle] = useDecision(FEATURE_FLAG.COOKIES_ACKNOWLEDGEMENT_BANNER)
  const [shouldShowBanner, setShouldShowBanner] = useState(false)
  const { pathname } = useRouter()
  const { t } = useTranslation('common')

  /**
   * Shouldn't show banner if the user has dismissed the cookie
   * It should check the cookies before starting to run geo-code processes
   * */
  const hasDismissedBanner = isServer()
    ? undefined
    : getCookieByKey(document.cookie, CookieRegistry.HAS_DISMISSED_COOKIE_ACKNOWLEDGEMENT_BANNER)

  useGetGeocodeReverseFromIpQuery({
    skip: !featureToggle.enabled || hasDismissedBanner === 'true',
    ssr: false,
    onCompleted: (data) => {
      setShouldShowBanner(data.geocodeReverseFromIp.province === ProvinceAbbreviation.Qc)
    },
    onError: (error) => {
      const errorMessage = error?.message || 'could not get location data'
      if (getGqlErrorStatusCode(error) !== HTTP_STATUS.NOT_FOUND) {
        sendToLogger(errorMessage, {
          tags: { fn: 'getUserLocation', component: 'CookieAcknowledgementBanner' },
          fingerprint: ['CookieAcknowledgementBanner'],
        })
      }
    },
  })

  const onCloseBanner = () => {
    createCookie(CookieRegistry.HAS_DISMISSED_COOKIE_ACKNOWLEDGEMENT_BANNER, 'true')

    setShouldShowBanner(false)
    trackEvent({ action: GA_EVENT.CookieAcknowledge, label: `loc=${pathname}` })
  }

  if (!shouldShowBanner) {
    return null
  }

  return (
    <>
      <CookieBannerBackgroundOverlay />
      <CookieBannerContent data-testid={'cookie-acknowledgement-banner'}>
        <CookieBannerDescription size="small">
          <Trans
            i18nKey="common:cookies.banner.description"
            components={[
              <LinkCustom
                hasUnderline
                href={`${t('cookies.banner.cookie_policy.kijiji.href')}`}
                key="cookiePolicyLink"
                rel="noreferrer"
                size="small"
                target="_blank"
                variant="secondary"
                noChildren={true}
              />,
              <LinkCustom
                hasUnderline
                href={`${t('cookies.banner.privacy_policy.kijiji.href')}`}
                key="privacyPolicyLink"
                rel="noreferrer"
                size="small"
                target="_blank"
                variant="secondary"
                noChildren={true}
              />,
            ]}
          />
        </CookieBannerDescription>
        <CookieBannerCloseButton onClick={onCloseBanner}>
          {t('cookies.banner.close')}
        </CookieBannerCloseButton>
      </CookieBannerContent>
    </>
  )
}
